import { homeSizes } from "@shared/heroImageConfig.json";
import { COMPANY_REVIEWS } from "@shared/companyFacts";
/*
 * STARTSIDA: /
 * Design: Nordisk Arkitektur-Editorial
 * Färger: Marinblå (navy) primär, Rosa (rose) CTA, Koppar accent, Sand bakgrund
 * Typografi: Poppins 700 rubriker, Nunito Sans 400/500 brödtext
 *
 * KUNDRESANS 4 FASER (scrollflöde):
 * Fas 1: Inspiration - "Vacker design för vackra stunder" (Hero + Galleri)
 * Fas 2: Övertygelse - "Kvalitet och hantverk från Sverige" (Varför oss + Guider)
 * Fas 3: Trygghet - "En process att lita på" (Process + Social Proof)
 * Fas 4: Ambassadörsskap - "Lever större" (Kundberättelser + CTA)
 *
 * PERSONAS:
 * Drömmaren (emotionell): Fångas av hero, bilder, poetisk copy
 * Realisten (rationell): Fångas av social proof, fakta, process
 */
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import SeoHead from "@/components/SeoHead";
import { getPageOgImage } from "@/lib/ogPageImages";
import TrustBar from "@/components/TrustBar";
import CtaSection from "@/components/CtaSection";
import SocialProof from "@/components/SocialProof";
import ProcessSteps from "@/components/ProcessSteps";
import ComparisonSection from "@/components/ComparisonSection";
import LazyVideo from "@/components/LazyVideo";
import { IMAGES } from "@/lib/images";
import {
  FadeUp,
  FadeIn,
  FadeLeft,
  FadeRight,
  ScaleIn,
  StaggerContainer,
  StaggerItem,
  HeroReveal,
} from "@/components/ScrollAnimations";
import ResponsiveImage, { CARD_SIZES } from "@/components/ResponsiveImage";
import {
  Phone,
  ArrowRight,
  CheckCircle,
  Star,
  Ruler,
  Shield,
  Thermometer,
  Sun,
  FileText,
  MapPin,
  ChevronRight,
  Factory,
  Sparkles,
  Heart,
} from "lucide-react";
import { useOffert } from "@/components/OffertModal";

const structuredData = [
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Nordiska Inglasningar",
    url: "https://nordiskauterum.se",
    logo: "https://nordiskauterum.se/manus-storage/logo-optimized_6798f633.webp",
    description:
      "Specialist på skräddarsydda uterum med erfarenhet sedan 2012 och cirka 18 000 inglasade balkonger.",
    telephone: "08-410 26 111",
    address: {
      "@type": "PostalAddress",
      streetAddress: "Abramsons väg 2",
      addressLocality: "Eskilstuna",
      postalCode: "632 39",
      addressCountry: "SE",
    },
    sameAs: [
      "https://www.nordiskainglasningar.se",
      "https://www.google.com/maps/place/Nordiska+Inglasningar",
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Nordiska Uterum",
    url: "https://nordiskauterum.se",
    description:
      "Allt om uterum: priser, bygglov, typer, inspiration och expertråd från Nordiska Inglasningar.",
  },
];

const services = [
  {
    title: "Uterum",
    desc: "Komplett guide om typer, konstruktion och fördelar med uterum.",
    href: "/uterum/",
    image: IMAGES.pulpet1,
    alt: "Uterum med pulpettak och glaspartier",
  },
  {
    title: "Pris & Kostnad",
    desc: "Uterum: Typer, konstruktion och paketlösningar från Nordiska Inglasningar.",
    href: "/uterum/vara-paket/",
    image: IMAGES.uterumRodTra,
    alt: "Uterum på röd villa med trädgård",
  },
  {
    title: "Bygglov (nya regler dec 2025)",
    desc: "Nya regler, attefallsregler och komplett guide till bygglovsprocessen.",
    href: "/uterum/bygglov/",
    image: IMAGES.heroBygglov,
    alt: "Svensk villa med uterum och trädgård",
  },
  {
    title: "Vinterbonat",
    desc: "Använd ditt uterum hela året med rätt isolering och uppvärmning.",
    href: "/uterum/vinterbonat/",
    image: IMAGES.heroVinterbonat,
    alt: "Vinterbonat uterum med snö utanför",
  },
  {
    title: "Glaspartier",
    desc: "Skjutdörrar, vikpartier och fasta glaspartier. Jämför alternativ.",
    href: "/uterum/glaspartier/",
    image: IMAGES.heroGlaspartier,
    alt: "Uterum med stora glaspartier",
  },
  {
    title: "Inspiration",
    desc: "Se bilder på genomförda projekt och 3D-visualiseringar.",
    href: "/uterum/inspiration/",
    image: IMAGES.houseDroneView,
    alt: "Drönarbild av villa med uterum",
  },
  {
    title: "Vår fabrik",
    desc: "Svensk tillverkning i Eskilstuna. Kvalitetskontroll i varje steg.",
    href: "/uterum/var-fabrik/",
    image: IMAGES.villa1,
    alt: "Nordiska Inglasningars fabrik i Eskilstuna",
  },
];

const cities = [
  { name: "Stockholm", href: "/uterum/stockholm/" },
  { name: "Göteborg", href: "/uterum/goteborg/" },
  { name: "Malmö", href: "/uterum/malmo/" },
  { name: "Uppsala", href: "/uterum/uppsala/" },
  { name: "Västerås", href: "/uterum/vasteras/" },
  { name: "Helsingborg", href: "/uterum/helsingborg/" },
  { name: "Örebro", href: "/uterum/orebro/" },
  { name: "Linköping", href: "/uterum/linkoping/" },
];

const projectImages = [
  { src: IMAGES.pulpet3, alt: "Pulpettak-uterum med möblerad interiör" },
  { src: IMAGES.villa2, alt: "Uterum med vita profiler på villa" },
  { src: IMAGES.inglasning2, alt: "Inglasad uteplats med möblering" },
  { src: IMAGES.skjutglas25, alt: "Skjutglas-uterum med trädgårdsutsikt" },
  { src: IMAGES.cornerEvening, alt: "Hörn-uterum med kvällsbelysning" },
  { src: IMAGES.houseArmchairs, alt: "Uterum med fåtöljer och mysig atmosfär" },
  { src: IMAGES.uterumDayFurnished, alt: "Möblerat uterum i dagsljus" },
  { src: IMAGES.poolVisualization, alt: "Uterum vid pool med glastak" },
];

export default function Home() {
  const { openOffert } = useOffert();

  return (
    <>
      <SeoHead
        title="Uterum: pris, bygglov och inspiration 2026"
        description="Skräddarsydda uterum från Nordiska Inglasningar. Priser, bygglov 2026, typer och inspiration. Erfarenhet sedan 2012, cirka 18 000 inglasade balkonger. Boka hembesök."
        path="/"
        structuredData={structuredData}
        keywords="uterum, uterum pris, bygglov uterum, vinterbonat uterum, glaspartier uterum, nordiska inglasningar"
        image={getPageOgImage("hem") || IMAGES.heroUterum}
      />

      {/* FAS 1: INSPIRATION - Vacker design for vackra stunder - Persona: Drommaren */}

      {/* HERO - Brand Story tonalitet */}
      <section className="relative min-h-[70vh] sm:min-h-[80vh] lg:min-h-[90vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <ResponsiveImage mobileSrc={IMAGES.heroUterumMobile} sizes={homeSizes}
              src={IMAGES.heroUterum}
              alt="Skandinaviskt uterum med glaspartier på svensk villa omgiven av natur"
              className="absolute inset-0 w-full h-full object-cover max-w-none"
              width={1280}
              height={720}
              fetchPriority="high"
              loading="eager"
              decoding="sync"
            />
          <div className="absolute inset-0 bg-gradient-to-r from-navy/90 via-navy/50 to-transparent" />
        </div>
        <div className="relative container pb-10 sm:pb-16 lg:pb-24 pt-24 sm:pt-32">
          <div className="max-w-3xl">
            <HeroReveal delay={0.1}>
              <p className="text-copper-light font-heading font-bold text-xs sm:text-sm lg:text-base tracking-widest uppercase mb-3 sm:mb-4">
                Varje uterum börjar med en längtan
              </p>
            </HeroReveal>
            <HeroReveal delay={0.25} instant>
              <h1 className="text-white text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold mb-3 sm:mb-4">
                Uterum &ndash; Skräddarsytt
                <span className="block text-copper-light">för dig</span>
              </h1>
            </HeroReveal>
            <HeroReveal delay={0.4}>
              <p className="text-white/80 text-base sm:text-lg lg:text-xl max-w-2xl mb-6 sm:mb-8 leading-relaxed">
                En dröm om de där stunderna som får livet att kännas lite
                större. Sorlet från vänner runt ett dukat bord, morgonkaffet i
                januarisol, närheten till naturen. Vi bygger förutsättningarna
                för nya minnen.
              </p>
            </HeroReveal>
            <HeroReveal delay={0.55}>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <Button
                  size="lg"
                  onClick={() => openOffert("hero-cta")}
                  className="bg-rose hover:bg-rose-dark text-white font-medium w-full sm:w-auto px-8 py-6 text-base"
                >
                  Börja din resa mot ett nytt rum
                </Button>
                <a
                  href="tel:+46841026111"
                  className="flex items-center justify-center sm:justify-start gap-2 text-white/80 hover:text-white text-base font-medium transition-colors py-3"
                >
                  <Phone className="w-5 h-5" />
                  Ring oss: 08-410 26 111
                </a>
              </div>
              <p className="text-white/70 text-xs sm:text-sm mt-3 sm:mt-4">
                Kostnadsfritt hembesök, helt kravlöst.
              </p>
            </HeroReveal>
          </div>
        </div>
      </section>

      {/* TRUST BAR */}
      <TrustBar />

      {/* BRAND STORY INTRO - Pelare 1: Vacker design för vackra stunder */}
      <section className="py-10 sm:py-16 lg:py-24 bg-warm-white">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <FadeLeft>
              <div>
                <p className="text-copper font-heading font-bold text-xs sm:text-sm tracking-widest uppercase mb-3">
                  Vacker design för vackra stunder
                </p>
                <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-navy mb-4 sm:mb-6">
                  Vi bygger inte bara väggar av glas, vi bygger
                  förutsättningarna för nya minnen
                </h2>
                <p className="text-muted-foreground text-base sm:text-lg leading-relaxed mb-4 sm:mb-6">
                  Det handlar om närheten till naturen och om att skapa ett mer
                  fulländat hem. Om doften av det första morgonkaffet i en
                  krispig januarisol, skyddad från vinden men mitt i ljuset. Om
                  sorlet från vänner runt ett dukat bord, långt efter att
                  sommarkvällen blivit sval.
                </p>
                <p className="text-muted-foreground text-base sm:text-lg leading-relaxed mb-6 sm:mb-8">
                  Med egen tillverkning i Eskilstuna hjälper vi dig att planera
                  ett uterum för ditt hus. Vi går igenom användning, mått och
                  förutsättningar så att lösningen och omfattningen blir tydliga
                  i din offert.
                </p>
                <div className="grid sm:grid-cols-2 gap-4">
                  {[
                    { icon: Sparkles, text: "Skräddarsytt efter dina drömmar" },
                    { icon: Shield, text: "Fast pris, inga dolda kostnader" },
                    { icon: Heart, text: "Från Förlängd Sommar till Boyta" },
                    { icon: Sun, text: "Kostnadsfritt hembesök" },
                  ].map(item => (
                    <div key={item.text} className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-sand flex items-center justify-center shrink-0">
                        <item.icon className="w-4.5 h-4.5 text-navy" />
                      </div>
                      <span className="text-navy font-medium text-sm">
                        {item.text}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </FadeLeft>
            <FadeRight>
              <div className="relative">
                <ResponsiveImage
                  src={IMAGES.projekt17}
                  alt="Modernt uterum med sadeltak och glaspartier på svensk villa"
                  className="w-full rounded-lg shadow-lg"
                  loading="lazy"
                  width={800}
                  height={600}
                  sizes="(max-width: 640px) 100vw, 560px"
                />
                <div className="absolute -bottom-4 -left-4 bg-navy text-white px-6 py-3 rounded-lg shadow-lg hidden lg:block">
                  <div className="flex items-center gap-2">
                    <Star className="w-5 h-5 text-copper-light fill-copper-light" />
                    <span className="font-heading font-bold text-lg">
                      {COMPANY_REVIEWS.ratingLabel}
                    </span>
                    <span className="text-white/70 text-sm">
                      {COMPANY_REVIEWS.countLabel}
                    </span>
                  </div>
                </div>
              </div>
            </FadeRight>
          </div>
        </div>
      </section>

      {/* PROJECT GALLERY - Fas 1 fortsättning */}
      <section className="py-10 sm:py-16 lg:py-24 bg-sand">
        <div className="container">
          <FadeUp>
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 sm:gap-4 mb-6 sm:mb-10">
              <div>
                <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-navy mb-1.5 sm:mb-2">
                  Genomförda projekt
                </h2>
                <p className="text-muted-foreground text-base sm:text-lg">
                  Bilder som visar olika lösningar för uterum.
                </p>
              </div>
              <Link
                href="/referenser/uterum/"
                className="inline-flex items-center gap-1 text-rose font-medium hover:gap-2 transition-all shrink-0"
              >
                Se alla referenser <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </FadeUp>
          <StaggerContainer
            className="grid grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-4"
            staggerDelay={0.08}
          >
            {projectImages.map((img, i) => (
              <StaggerItem key={i}>
                <div className="aspect-[4/3] overflow-hidden rounded-lg">
                  <ResponsiveImage
                    src={img.src}
                    alt={img.alt}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                    width={800}
                    height={600}
                    sizes="(min-width: 1280px) 384px, (min-width: 1024px) calc((100vw - 96px) / 3), calc((100vw - 48px) / 2)"
                  />
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* EMOTIONELL VIDEO - Brö mellan Inspiration och Övertygelse */}
      <section className="py-10 sm:py-16 lg:py-20 bg-navy">
        <div className="container max-w-4xl">
          <FadeUp>
            <div className="text-center mb-6 sm:mb-10">
              <p className="text-copper-light font-heading font-bold text-xs sm:text-sm tracking-widest uppercase mb-3">
                Kundberättelser
              </p>
              <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3 sm:mb-4">
                Känn hur det är att leva med ett uterum
              </h2>
              <p className="text-white/70 text-base sm:text-lg leading-relaxed max-w-2xl mx-auto">
                Regn utanför, värme innanför. Se hur våra kunder njuter av sina
                uterum året runt.
              </p>
            </div>
          </FadeUp>
          <FadeUp delay={0.15}>
            <div className="rounded-xl overflow-hidden shadow-2xl">
              <LazyVideo
                src={IMAGES.videoAllaArstider}
                poster={IMAGES.case3dRottHus}
                className="w-full aspect-video object-cover"
              />
            </div>
            <p className="text-white/70 text-sm text-center mt-4">
              Uterum för alla årstider – en familj njuter av fika medan regnet
              smattrar på taket
            </p>
          </FadeUp>
        </div>
      </section>

      {/* FAS 2: OVERTYGELSE - Kvalitet och hantverk fran Sverige - Persona: Realisten */}

      {/* WHY CHOOSE US - Pelare 2: Kvalitet och hantverk */}
      <section className="py-10 sm:py-16 lg:py-24 bg-white">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center">
            <FadeLeft>
              <div>
                <p className="text-copper font-heading font-bold text-xs sm:text-sm tracking-widest uppercase mb-3">
                  Kvalitet och hantverk från Sverige
                </p>
                <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-navy mb-4 sm:mb-6">
                  Svenskt hantverk, kompromisslös kvalitet
                </h2>
                <p className="text-muted-foreground text-base sm:text-lg leading-relaxed mb-6">
                  Vi vet att en dröm är värd att förvaltas med omsorg. Därför är
                  vår tekniska precision och kompromisslösa kvalitet vår
                  viktigaste grundbult. Våra produkter är skapade för att vara
                  vackra nog att stoltsera med, men intelligenta nog att bara
                  fungera.
                </p>
                <div className="space-y-5">
                  {[
                    {
                      title: "Egen fabrik i Sverige",
                      desc: "Vi äger hela värdekedjan från innovation och egen fabrik till slutmontage. Full kontroll över kvalitet och leveranstider.",
                      icon: Factory,
                    },
                    {
                      title: "LiDAR-precision",
                      desc: "Millimeterprecis uppmätning med den senaste tekniken. Varje uterum skräddarsys efter ditt hus.",
                      icon: Ruler,
                    },
                    {
                      title: "erfarenhet sedan 2012",
                      desc: "Sedan 2012 har vi specialiserat oss på uterum och inglasningar för svenska förhållanden. Cirka 18 000 inglasade balkonger.",
                      icon: Shield,
                    },
                    {
                      title: "Patenterade lösningar",
                      desc: "Egenutvecklade, patenterade system som är designade för det nordiska klimatet.",
                      icon: Sparkles,
                    },
                  ].map(item => (
                    <div key={item.title} className="flex gap-4">
                      <div className="w-10 h-10 rounded-full bg-sand flex items-center justify-center shrink-0 mt-0.5">
                        <item.icon className="w-5 h-5 text-navy" />
                      </div>
                      <div>
                        <h3 className="font-heading font-bold text-lg text-navy mb-1">
                          {item.title}
                        </h3>
                        <p className="text-muted-foreground leading-relaxed text-sm sm:text-base">
                          {item.desc}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </FadeLeft>
            <FadeRight delay={0.15}>
              <div className="grid grid-cols-2 gap-2 sm:gap-4">
                <ResponsiveImage
                  src={IMAGES.projekt46}
                  alt="Uterum med glaspartier och trädäck"
                  className="w-full rounded-lg col-span-2"
                  loading="lazy"
                  width={800}
                  height={600}
                  sizes="(max-width: 1024px) 100vw, 600px"
                />
                <ResponsiveImage
                  src={IMAGES.projekt27_1}
                  alt="Uterum interiör med matplats"
                  className="w-full rounded-lg"
                  loading="lazy"
                  width={800}
                  height={600}
                  sizes="(max-width: 1024px) 50vw, 300px"
                />
                <ResponsiveImage
                  src={IMAGES.projekt41}
                  alt="Uterum med panoramautsikt"
                  className="w-full rounded-lg"
                  loading="lazy"
                  width={800}
                  height={600}
                  sizes="(max-width: 1024px) 50vw, 300px"
                />
              </div>
            </FadeRight>
          </div>
        </div>
      </section>

      {/* COMPARISON: Helhetslösning vs DIY */}
      <ComparisonSection />

      {/* SERVICES GRID - Guider */}
      <section className="py-10 sm:py-16 lg:py-24 bg-sand">
        <div className="container">
          <FadeUp>
            <div className="text-center max-w-2xl mx-auto mb-8 sm:mb-12">
              <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-navy mb-3 sm:mb-4">
                Utforska våra guider
              </h2>
              <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
                Oavsett om du är i planeringsfasen eller redo att beställa, har
                vi samlat all information du behöver.
              </p>
            </div>
          </FadeUp>
          <StaggerContainer className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {services.map(service => (
              <StaggerItem key={service.href}>
                <Link
                  href={service.href}
                  className="group bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 block h-full border border-transparent hover:border-navy/10"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <ResponsiveImage
                      src={service.image}
                      alt={service.alt}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                      width={800}
                      height={600}
                      sizes={CARD_SIZES}
                    />
                  </div>
                  <div className="p-4 sm:p-6">
                    <h3 className="font-heading text-lg sm:text-xl font-bold text-navy mb-1.5 sm:mb-2 group-hover:text-rose transition-colors">
                      {service.title}
                    </h3>
                    <p className="text-muted-foreground text-sm leading-relaxed mb-3">
                      {service.desc}
                    </p>
                    <span className="inline-flex items-center gap-1 text-rose text-sm font-medium group-hover:gap-2 transition-all">
                      Läs mer <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </Link>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* VÅRA PAKET section hidden (2026-07-24) */}

      {/* FAS 3: TRYGGHET - En process att lita pa - Persona: Bada */}

      {/* PROCESS STEPS - Stora dekorativa siffror */}
      <ProcessSteps />

      {/* SOCIAL PROOF - 1600+ recensioner */}
      <SocialProof />

      {/* FAS 4: AMBASSADORSSKAP - Lever storre - Kundberattelser + CTA */}

      {/* LOCAL PAGES */}
      <section className="py-10 sm:py-16 lg:py-20 bg-sand">
        <div className="container">
          <FadeUp>
            <div className="text-center max-w-2xl mx-auto mb-6 sm:mb-10">
              <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-navy mb-3 sm:mb-4">
                Uterum i din stad
              </h2>
              <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
                Vi bygger uterum i hela Sverige. Hitta lokal information om
                priser, bygglov och förutsättningar i din stad.
              </p>
            </div>
          </FadeUp>
          <StaggerContainer
            className="grid grid-cols-2 sm:grid-cols-4 gap-3"
            staggerDelay={0.06}
          >
            {cities.map(city => (
              <StaggerItem key={city.href}>
                <Link
                  href={city.href}
                  className="flex items-center justify-between px-3 sm:px-5 py-3 sm:py-4 bg-white rounded-lg hover:bg-sand-dark hover:shadow-sm hover:-translate-y-0.5 transition-all duration-200 group"
                >
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-rose" />
                    <span className="font-medium text-navy text-sm">
                      {city.name}
                    </span>
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-navy transition-colors" />
                </Link>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* QUICK LINKS */}
      <section className="py-10 sm:py-12 lg:py-16 bg-white">
        <div className="container">
          <StaggerContainer className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
            <StaggerItem>
              <Link
                href="/uterum/checklista/"
                className="flex items-center gap-3 sm:gap-4 bg-sand p-4 sm:p-6 rounded-lg hover:shadow-md hover:-translate-y-1 transition-all duration-250 group"
              >
                <div className="w-12 h-12 rounded-full bg-navy/10 flex items-center justify-center shrink-0">
                  <FileText className="w-6 h-6 text-navy" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-navy group-hover:text-rose transition-colors">
                    Checklista
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    Planera ditt uterumsprojekt steg för steg
                  </p>
                </div>
              </Link>
            </StaggerItem>
            <StaggerItem>
              <Link
                href="/uterum/uterum-vs-orangeri/"
                className="flex items-center gap-3 sm:gap-4 bg-sand p-4 sm:p-6 rounded-lg hover:shadow-md hover:-translate-y-1 transition-all duration-250 group"
              >
                <div className="w-12 h-12 rounded-full bg-navy/10 flex items-center justify-center shrink-0">
                  <Sun className="w-6 h-6 text-navy" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-navy group-hover:text-rose transition-colors">
                    Uterum vs Orangeri
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    Skillnader, fördelar och nackdelar
                  </p>
                </div>
              </Link>
            </StaggerItem>
            <StaggerItem>
              <Link
                href="/uterum/fragor/"
                className="flex items-center gap-3 sm:gap-4 bg-sand p-4 sm:p-6 rounded-lg hover:shadow-md hover:-translate-y-1 transition-all duration-250 group"
              >
                <div className="w-12 h-12 rounded-full bg-navy/10 flex items-center justify-center shrink-0">
                  <FileText className="w-6 h-6 text-navy" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-navy group-hover:text-rose transition-colors">
                    Vanliga frågor
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    100 artiklar med svar på allt om uterum
                  </p>
                </div>
              </Link>
            </StaggerItem>
          </StaggerContainer>
        </div>
      </section>

      {/* POPULÄRA FRÅGOR - Direktlänkar till FAQ-artiklar för intern länkning */}
      <section className="py-10 sm:py-14 lg:py-16 bg-sand/30">
        <div className="container">
          <FadeUp>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-navy mb-2">
              Populära frågor om uterum
            </h2>
            <p className="text-muted-foreground font-body mb-8 max-w-2xl">
              Svar på de vanligaste frågorna om bygglov, priser och
              konstruktion.
            </p>
          </FadeUp>
          <StaggerContainer className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            <StaggerItem>
              <Link
                href="/uterum/fragor/behover-du-bygglov-for-ett-uterum/"
                className="block p-4 sm:p-5 bg-white rounded-lg border border-border hover:border-navy/30 hover:shadow-md transition-all group"
              >
                <h3 className="font-heading font-semibold text-sm sm:text-base text-foreground group-hover:text-navy transition-colors leading-snug">
                  Behöver du bygglov för ett uterum?
                </h3>
              </Link>
            </StaggerItem>
            <StaggerItem>
              <Link
                href="/uterum/fragor/hur-stort-uterum-far-man-bygga-utan-bygglov/"
                className="block p-4 sm:p-5 bg-white rounded-lg border border-border hover:border-navy/30 hover:shadow-md transition-all group"
              >
                <h3 className="font-heading font-semibold text-sm sm:text-base text-foreground group-hover:text-navy transition-colors leading-snug">
                  Hur stort uterum får man bygga utan bygglov?
                </h3>
              </Link>
            </StaggerItem>
            <StaggerItem>
              <Link
                href="/uterum/fragor/vad-innebar-nya-bygglovsregler-2026/"
                className="block p-4 sm:p-5 bg-white rounded-lg border border-border hover:border-navy/30 hover:shadow-md transition-all group"
              >
                <h3 className="font-heading font-semibold text-sm sm:text-base text-foreground group-hover:text-navy transition-colors leading-snug">
                  Vad innebär nya bygglovsregler 2026?
                </h3>
              </Link>
            </StaggerItem>
            <StaggerItem>
              <Link
                href="/uterum/fragor/hur-mycket-far-man-bygga-utan-bygglov-2026/"
                className="block p-4 sm:p-5 bg-white rounded-lg border border-border hover:border-navy/30 hover:shadow-md transition-all group"
              >
                <h3 className="font-heading font-semibold text-sm sm:text-base text-foreground group-hover:text-navy transition-colors leading-snug">
                  Hur mycket får man bygga utan bygglov 2026?
                </h3>
              </Link>
            </StaggerItem>
            <StaggerItem>
              <Link
                href="/uterum/fragor/far-man-glasa-in-altanen-utan-bygglov/"
                className="block p-4 sm:p-5 bg-white rounded-lg border border-border hover:border-navy/30 hover:shadow-md transition-all group"
              >
                <h3 className="font-heading font-semibold text-sm sm:text-base text-foreground group-hover:text-navy transition-colors leading-snug">
                  Får man glasa in altanen utan bygglov?
                </h3>
              </Link>
            </StaggerItem>
            <StaggerItem>
              <Link
                href="/uterum/fragor/kan-man-ha-kamin-i-uterum/"
                className="block p-4 sm:p-5 bg-white rounded-lg border border-border hover:border-navy/30 hover:shadow-md transition-all group"
              >
                <h3 className="font-heading font-semibold text-sm sm:text-base text-foreground group-hover:text-navy transition-colors leading-snug">
                  Kan man ha kamin i uterum?
                </h3>
              </Link>
            </StaggerItem>
            <StaggerItem>
              <Link
                href="/uterum/fragor/vad-kostar-det-att-glasa-in-en-altan/"
                className="block p-4 sm:p-5 bg-white rounded-lg border border-border hover:border-navy/30 hover:shadow-md transition-all group"
              >
                <h3 className="font-heading font-semibold text-sm sm:text-base text-foreground group-hover:text-navy transition-colors leading-snug">
                  Vad kostar det att glasa in en altan?
                </h3>
              </Link>
            </StaggerItem>
            <StaggerItem>
              <Link
                href="/uterum/fragor/hur-mycket-kostar-det-att-bygga-ett-uterum/"
                className="block p-4 sm:p-5 bg-white rounded-lg border border-border hover:border-navy/30 hover:shadow-md transition-all group"
              >
                <h3 className="font-heading font-semibold text-sm sm:text-base text-foreground group-hover:text-navy transition-colors leading-snug">
                  Hur mycket kostar det att bygga ett uterum?
                </h3>
              </Link>
            </StaggerItem>
            <StaggerItem>
              <Link
                href="/uterum/uterum-vs-orangeri/"
                className="block p-4 sm:p-5 bg-white rounded-lg border border-border hover:border-navy/30 hover:shadow-md transition-all group"
              >
                <h3 className="font-heading font-semibold text-sm sm:text-base text-foreground group-hover:text-navy transition-colors leading-snug">
                  Uterum vs orangeri: skillnader och likheter
                </h3>
              </Link>
            </StaggerItem>
            <StaggerItem>
              <Link
                href="/uterum/fragor/paverkar-ett-uterum-fastighetsskatten/"
                className="block p-4 sm:p-5 bg-white rounded-lg border border-border hover:border-navy/30 hover:shadow-md transition-all group"
              >
                <h3 className="font-heading font-semibold text-sm sm:text-base text-foreground group-hover:text-navy transition-colors leading-snug">
                  Påverkar ett uterum fastighetsskatten?
                </h3>
              </Link>
            </StaggerItem>
            <StaggerItem>
              <Link
                href="/uterum/fragor/behover-man-isolera-ett-uterum/"
                className="block p-4 sm:p-5 bg-white rounded-lg border border-border hover:border-navy/30 hover:shadow-md transition-all group"
              >
                <h3 className="font-heading font-semibold text-sm sm:text-base text-foreground group-hover:text-navy transition-colors leading-snug">
                  Behöver man isolera ett uterum?
                </h3>
              </Link>
            </StaggerItem>
            <StaggerItem>
              <Link
                href="/uterum/fragor/vad-ar-skillnaden-mellan-attefallshus-och-komplementbostadshus/"
                className="block p-4 sm:p-5 bg-white rounded-lg border border-border hover:border-navy/30 hover:shadow-md transition-all group"
              >
                <h3 className="font-heading font-semibold text-sm sm:text-base text-foreground group-hover:text-navy transition-colors leading-snug">
                  Vad är skillnaden mellan attefallshus och uterum?
                </h3>
              </Link>
            </StaggerItem>
          </StaggerContainer>
          <FadeUp>
            <div className="mt-6 text-center">
              <Link
                href="/uterum/fragor/"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-navy hover:text-rose transition-colors font-body"
              >
                Se alla frågor och svar <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* CTA - Emotionell tonalitet */}
      <CtaSection
        variant="dark"
        title="Varje dröm börjar med ett samtal"
        subtitle="Vi förstår att resan mot ett nytt rum är en stor händelse. Boka ett kostnadsfritt hembesök så lyssnar vi på dina drömmar och ser förutsättningarna på plats. Helt kravlöst."
        seasonalContext="hero"
        showUrgency
      />
    </>
  );
}
